import type { FormFieldAPI } from "@/shared/forms";
import { useFieldContext } from "./FieldContext";
import { FormFieldConnector } from "./FormFieldConnector";
import {
    LimitedValuesField,
    NumberField,
    PlaceAutocompleteField,
    StringField,
    ValueRangeField,
    YearField,
} from "./basic-fields";
import { FieldComposerFieldType } from "./types";

type Props = {
    field: FormFieldAPI<any>;
    inputProperties?: React.InputHTMLAttributes<HTMLInputElement>;
};

function GenericFieldMapper({ field, inputProperties }: Props) {
    const { fieldConfig, fieldHtmlId, fieldPath } = useFieldContext();
    const { fieldType, label, required } = fieldConfig;
    const commonProperties = {
        "data-testid": fieldPath,
    };

    if (!field) return null;

    const placeholder = typeof label === "string" ? label : "";
    switch (fieldType) {
        case FieldComposerFieldType.Place:
            return (
                <PlaceAutocompleteField
                    id={fieldHtmlId}
                    {...inputProperties}
                    {...field}
                    {...commonProperties}
                    data-testid={`placeInput-${fieldPath}`}
                    placeholder={fieldConfig.placeholder || placeholder}
                    required={required}
                />
            );
        case FieldComposerFieldType.Year:
            return (
                <YearField
                    id={fieldHtmlId}
                    {...inputProperties}
                    {...field}
                    {...commonProperties}
                    placeholder={placeholder}
                    required={required}
                />
            );
        case FieldComposerFieldType.ValueRange:
            return (
                <ValueRangeField
                    {...commonProperties}
                    {...inputProperties}
                    id={fieldHtmlId}
                    ref={field.ref}
                    value={field.value as string}
                    onChange={({ target }) => field.onChange(target.value)}
                    valueRange={fieldConfig.range}
                    required={required}
                />
            );
        case FieldComposerFieldType.LimitedValues:
            return (
                <LimitedValuesField
                    {...commonProperties}
                    {...inputProperties}
                    id={fieldHtmlId}
                    ref={field.ref}
                    value={field.value}
                    onChange={({ target }) => field.onChange(target.value)}
                    limitedValues={fieldConfig.options}
                    required={required}
                />
            );
        case FieldComposerFieldType.Integer:
            return (
                <NumberField
                    id={fieldHtmlId}
                    {...commonProperties}
                    {...inputProperties}
                    validationPattern="number"
                    ref={field.ref}
                    value={field.value}
                    onChange={field.onChange}
                    required={required}
                />
            );
        default:
            return (
                <StringField
                    id={fieldHtmlId}
                    {...commonProperties}
                    {...inputProperties}
                    ref={field.ref}
                    value={field.value as string}
                    onChange={field.onChange}
                    placeholder={fieldConfig.placeholder}
                    required={required}
                />
            );
    }
}

export function GenericField(inputProperties?: React.InputHTMLAttributes<HTMLInputElement>) {
    return (
        <FormFieldConnector>
            {({ formField }) => <GenericFieldMapper field={formField} inputProperties={inputProperties} />}
        </FormFieldConnector>
    );
}
